import { useEffect } from 'react';
import { toast } from 'sonner';
import { useCall } from '../../contexts/CallContext';
import { useGroupCall } from '../../contexts/GroupCallContext';
import { createConversation } from '../../api/chat';

/**
 * 1:1 → group escalation. CallProvider wraps GroupCallProvider, so CallScreen
 * (rendered by CallProvider) cannot reach useGroupCall directly. This bridge
 * lives INSIDE GroupCallProvider and registers the escalation with CallContext.
 * A group that continues the 1:1 keeps the person already on it from ringing.
 */
export default function CallEscalationBridge() {
  const { bindEscalate, hangup } = useCall();
  const { startGroupCall } = useGroupCall();

  useEffect(() => {
    bindEscalate(async ({ callId, peerUserId, peerName, newUserIds }) => {
      try {
        const groupName = peerName ? `${peerName} +${newUserIds.length}` : 'Group call';
        const conv = await createConversation({
          type: 'call',
          name: groupName,
          member_ids: [peerUserId, ...newUserIds],
        });
        if (callId) {
          // The group continues this 1:1: the server ends it and the peer
          // joins without ringing. The 1:1 stays up until then.
          const result = await startGroupCall(conv.id, { continuesCallId: callId });
          if (result === 'started') return;
          if (result === 'failed') throw new Error('Could not add people to the call');
        }
        // An older server: end the 1:1, then ring everyone as a new group.
        hangup();                       // end the 1:1 (logs its outcome + call_event)
        await startGroupCall(conv.id);  // re-ring peer + new people as a group
      } catch {
        toast.error('Could not add participant');
      }
    });
  }, [bindEscalate, hangup, startGroupCall]);

  return null;
}
